<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { CLUSTERED_TYPES, PIE_TYPES, isNpsTemplate, isNpsCard } from "@stores/chartTemplates/chartTemplates";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import ChartPreview from "@/components/Reports/ChartTemplates/ChartPreview.vue";
  import FilterSelector from "./FilterSelector.vue";
  import CrosstabSelector from "./CrosstabSelector.vue";

  const { currentReport } = storeToRefs(useReportsV2Store());

  const props = defineProps({
    fieldName: {
      type: String,
      required: true,
    },
    binding: {
      type: Object,
      default: () => ({}),
    },
    chartableCards: {
      type: Array,
      default: () => [],
    },
    chartTemplates: {
      type: Array,
      default: () => [],
    },
    targetOptions: {
      type: Array,
      default: () => [],
    },
    populationOptions: {
      type: Array,
      default: () => [],
    },
    crosstabQuestionOptions: {
      type: Array,
      default: () => [],
    },
    groupOptions: {
      type: Array,
      default: () => [],
    },
    getCompatibleBaseTypes: {
      type: Function,
      required: true,
    },
    isCrosstabCompatible: {
      type: Function,
      required: true,
    },
  });

  const emit = defineEmits(["update:binding"]);

  // Computed properties
  const cardOptions = computed(() =>
    props.chartableCards.map((card) => ({
      value: card.card_token,
      label: `${card.name} (${card.question_type}) - ${card.title}`,
    })),
  );

  const sourceCard = computed(() => {
    const cardToken = props.binding.card_token;
    if (!cardToken) return null;
    return props.chartableCards.find((c) => c.card_token === cardToken) || null;
  });

  const questionType = computed(() => sourceCard.value?.question_type || null);

  const isMatrixQuestion = computed(() => questionType.value === "matrix");
  const isMatrixMultiple = computed(() => isMatrixQuestion.value && sourceCard.value?.matrix_type === "multiple");

  const matrixItemOptions = computed(() =>
    (sourceCard.value?.matrix_items || []).map((item) => ({
      value: item.item_token,
      label: item.title || item.item_token,
    })),
  );

  const selectedMatrixItem = computed(
    () => props.binding.matrix_item_token || matrixItemOptions.value[0]?.value,
  );

  const selectedMatrixItems = computed(
    () => props.binding.matrix_item_tokens || matrixItemOptions.value.map((o) => o.value),
  );

  const handleMatrixItemsChange = (tokens) => {
    // Omitted binding key = all items (robust to items added later)
    const all = matrixItemOptions.value.map((o) => o.value);
    updateBinding({ matrix_item_tokens: tokens?.length && tokens.length < all.length ? tokens : null });
  };

  const hasFilter = computed(() => {
    return props.binding.add_filters === true || !!props.binding.filter_group_token;
  });

  const hasCrosstab = computed(() => {
    const binding = props.binding;
    return binding.crosstab_enabled || binding.crosstab_question_id || binding.crosstab_groups_tokens?.length > 0;
  });

  const crosstabType = computed(() => {
    const binding = props.binding;
    if (binding.crosstab_type) return binding.crosstab_type;
    if (binding.crosstab_groups_tokens?.length > 0) return "groups";
    return "question";
  });

  const hasReportFilterDefault = computed(() => {
    const filters = currentReport.value?.global_filters;
    return !!(filters?.target_id || filters?.group_token);
  });

  const hasReportCrosstabDefault = computed(() => {
    const crosstab = currentReport.value?.global_crosstab;
    return !!(crosstab?.question_id || crosstab?.groups_tokens?.length);
  });

  function getChartPalette(tpl) {
    const chartStyles = currentReport.value?.report_theme_chart_styles || {};
    // undefined (not []) when the theme has no palette, so ChartPreview falls back to its default colors
    const palette = tpl.report_theme_palette === "nps" ? chartStyles.palette_nps : chartStyles.palette_default;
    return palette?.length ? palette : undefined;
  }

  // Helper to update binding
  function updateBinding(updates) {
    emit("update:binding", { ...props.binding, ...updates });
  }

  // Event handlers
  function handleCardChange(cardToken) {
    const card = props.chartableCards.find((c) => c.card_token === cardToken);
    const qType = card?.question_type || null;

    const newBinding = {
      type: "question",
      card_token: cardToken || null,
      add_filters: props.binding.add_filters || false,
      filter_target_id: props.binding.filter_target_id || null,
      filter_group_token: props.binding.filter_group_token || null,
      crosstab_enabled: qType === "matrix" ? false : props.binding.crosstab_enabled || false,
      crosstab_type: props.binding.crosstab_type || null,
      crosstab_question_id: props.binding.crosstab_question_id || null,
      crosstab_groups_tokens: props.binding.crosstab_groups_tokens || [],
    };

    // Auto-select first compatible chart template
    if (cardToken && props.chartTemplates.length > 0 && qType) {
      const compatibleTemplates = props.chartTemplates.filter(
        (t) => t.is_enabled && isTemplateCompatibleWith(t, card),
      );
      if (compatibleTemplates.length > 0) {
        newBinding.chart_template_id = compatibleTemplates[0].id;
      }
    }

    emit("update:binding", newBinding);
  }

  function handleFilterToggle(enabled) {
    const updates = { add_filters: enabled };
    if (!enabled) {
      updates.filter_target_id = null;
      updates.filter_group_token = null;
    }
    updateBinding(updates);
  }

  function handleCrosstabToggle(enabled) {
    if (enabled) {
      const updates = {
        crosstab_enabled: true,
        crosstab_type: "question",
        crosstab_question_id: null,
        crosstab_groups_tokens: [],
      };

      // Reset chart template if incompatible with crosstab
      const currentTemplateId = props.binding.chart_template_id;
      if (currentTemplateId) {
        const currentTemplate = props.chartTemplates.find((t) => t.id === currentTemplateId);
        if (currentTemplate && !props.isCrosstabCompatible(currentTemplate.base_type)) {
          const compatibleTemplate = props.chartTemplates.find(
            (t) =>
              t.is_enabled &&
              isTemplateCompatibleWith(t, sourceCard.value) &&
              props.isCrosstabCompatible(t.base_type),
          );
          if (compatibleTemplate) {
            updates.chart_template_id = compatibleTemplate.id;
          }
        }
      }
      updateBinding(updates);
    } else {
      updateBinding({
        crosstab_enabled: false,
        crosstab_type: null,
        crosstab_question_id: null,
        crosstab_groups_tokens: [],
      });
    }
  }

  function handleCrosstabTypeChange(type) {
    const updates = { crosstab_type: type };
    if (type === "question") {
      updates.crosstab_groups_tokens = [];
    } else {
      updates.crosstab_question_id = null;
    }
    updateBinding(updates);
  }

  // Template helpers
  function isTemplateCompatibleWith(template, card) {
    if (!card) return true;
    if (!props.getCompatibleBaseTypes(card.question_type, card.matrix_type).includes(template.base_type)) return false;
    if (isNpsTemplate(template) && !isNpsCard(card)) return false;
    return true;
  }

  function isTemplateDisabled(template) {
    if (sourceCard.value && !isTemplateCompatibleWith(template, sourceCard.value)) {
      return true;
    }
    if (hasCrosstab.value && !props.isCrosstabCompatible(template.base_type)) {
      return true;
    }
    return false;
  }

  function getTemplateDisabledReason(template) {
    if (sourceCard.value) {
      if (isNpsTemplate(template) && !isNpsCard(sourceCard.value)) {
        return "Reserved for NPS questions";
      }
      if (!props.getCompatibleBaseTypes(questionType.value, sourceCard.value?.matrix_type).includes(template.base_type)) {
        return "Incompatible with this question type";
      }
    }
    if (hasCrosstab.value && !props.isCrosstabCompatible(template.base_type)) {
      return "Incompatible with cross-tabulation (single series only)";
    }
    return "";
  }

  function getTemplateClasses(template) {
    const isSelected = props.binding.chart_template_id === template.id;
    const isDisabled = isTemplateDisabled(template);

    if (isDisabled) return "cursor-not-allowed";
    if (isSelected) return "cursor-pointer ring-2 ring-primary";
    return "cursor-pointer hover:border-primary/50";
  }

  function getPreviewSeriesCount(template) {
    if (isNpsTemplate(template)) return 3;
    return CLUSTERED_TYPES.includes(template.base_type) ? 1 : 4;
  }

  function getPreviewCategoryCount(template) {
    if (PIE_TYPES.includes(template.base_type)) return 4;
    return CLUSTERED_TYPES.includes(template.base_type) ? 4 : 1;
  }
</script>

<template>
  <div class="space-y-4">
    <!-- Source card -->
    <div class="w-1/2 min-w-0">
      <Label class="text-sm mb-1.5">Source card</Label>
      <Select
        :model-value="binding.card_token || undefined"
        :options="cardOptions"
        filterable
        placeholder="Select a card"
        @update:model-value="handleCardChange($event)" />
    </div>

    <!-- Matrix item selection -->
    <div v-if="isMatrixMultiple && matrixItemOptions.length" class="w-1/2 min-w-0">
      <Label class="text-sm mb-1.5">Item</Label>
      <Select
        :model-value="selectedMatrixItem"
        :options="matrixItemOptions"
        placeholder="Select an item"
        @update:model-value="updateBinding({ matrix_item_token: $event })" />
    </div>
    <div v-else-if="isMatrixQuestion && matrixItemOptions.length" class="w-1/2 min-w-0">
      <Label class="text-sm mb-1.5">Items</Label>
      <Select
        :model-value="selectedMatrixItems"
        :options="matrixItemOptions"
        multiple
        placeholder="Select items"
        @update:model-value="handleMatrixItemsChange($event)" />
    </div>

    <!-- Filter respondents -->
    <FilterSelector
      :card-token="binding.card_token"
      :has-filter="hasFilter"
      :target-id="binding.filter_target_id"
      :group-token="binding.filter_group_token"
      :target-options="targetOptions"
      :population-options="populationOptions"
      :has-report-default="hasReportFilterDefault"
      @update:has-filter="handleFilterToggle"
      @update:target-id="updateBinding({ filter_target_id: $event })"
      @update:group-token="updateBinding({ filter_group_token: $event })" />

    <!-- Crosstabs -->
    <CrosstabSelector
      :field-name="fieldName"
      :card-token="binding.card_token"
      :is-matrix-question="isMatrixQuestion"
      :has-crosstab="hasCrosstab"
      :crosstab-type="crosstabType"
      :crosstab-question-id="binding.crosstab_question_id"
      :crosstab-group-tokens="binding.crosstab_groups_tokens || []"
      :crosstab-question-options="crosstabQuestionOptions"
      :group-options="groupOptions"
      :has-report-default="hasReportCrosstabDefault"
      @update:has-crosstab="handleCrosstabToggle"
      @update:crosstab-type="handleCrosstabTypeChange"
      @update:crosstab-question-id="updateBinding({ crosstab_question_id: $event, crosstab_groups_tokens: [] })"
      @update:crosstab-group-tokens="updateBinding({ crosstab_groups_tokens: $event, crosstab_question_id: null })" />

    <!-- Chart template selection -->
    <div
      v-if="binding.card_token"
      class="space-y-4">
      <div>
        <Label class="text-sm">Chart template</Label>
        <div class="mt-2 grid grid-cols-3 gap-2">
          <div
            v-for="tpl in chartTemplates"
            :key="tpl.id"
            class="rounded-lg border bg-card overflow-hidden transition-all"
            :class="getTemplateClasses(tpl)"
            :title="getTemplateDisabledReason(tpl)"
            @click="!isTemplateDisabled(tpl) && updateBinding({ chart_template_id: tpl.id })">
            <!-- Mini preview -->
            <div
              class="bg-white flex items-center justify-center aspect-[16/10]"
              :class="{ 'opacity-30': isTemplateDisabled(tpl) }">
              <ChartPreview
                :base-type="tpl.base_type"
                :colors="getChartPalette(tpl)"
                :width="100"
                :height="60"
                :series-count="getPreviewSeriesCount(tpl)"
                :category-count="getPreviewCategoryCount(tpl)"
                class="w-full h-auto" />
            </div>
            <!-- Name only -->
            <div
              class="px-2 py-1 border-t text-3xs text-foreground truncate"
              :class="{ 'opacity-30': isTemplateDisabled(tpl) }">
              {{ tpl.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
